import DataTable from "app/components/DataTable";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { RegistrationRow } from "../../types";
import { useStyles } from "./styles";

type Props = {
  rows: RegistrationRow[];
  loading?: boolean;
};

const formatCellValue = (
  value: string | number | null | undefined
): string | number => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return value;
};

const renderValue = (
  value: string | number | null | undefined,
  styles: ReturnType<typeof useStyles>
) => <span className={styles.textCell}>{formatCellValue(value)}</span>;

const RegistrationTable = ({ rows, loading = false }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const columns = useMemo(
    () => [
      {
        id: "cabinType",
        label: t("REGISTRATION.TABLE.DRIVER_CABIN_TYPE"),
        align: "right",
        minWidth: 110,
        format: (row: RegistrationRow) =>
          renderValue(row.cabinType, styles),
      },
      {
        id: "tireSize",
        label: t("REGISTRATION.TABLE.TIRE"),
        align: "right",
        minWidth: 72,
        format: (row: RegistrationRow) => renderValue(row.tireSize, styles),
      },
      {
        id: "hangerType",
        label: t("REGISTRATION.TABLE.SUSPENSION_TYPE"),
        align: "right",
        minWidth: 100,
        format: (row: RegistrationRow) =>
          renderValue(row.hangerType, styles),
      },
      {
        id: "weight",
        label: t("REGISTRATION.TABLE.WEIGHT"),
        align: "right",
        minWidth: 80,
        format: (row: RegistrationRow) => renderValue(row.weight, styles),
      },
      {
        id: "wealBase",
        label: t("REGISTRATION.TABLE.AXLES_MEASURE"),
        align: "right",
        minWidth: 96,
        format: (row: RegistrationRow) => renderValue(row.wealBase, styles),
      },
      {
        id: "lowerRange",
        label: t("REGISTRATION.TABLE.LOWER_LIMIT"),
        align: "right",
        minWidth: 100,
        format: (row: RegistrationRow) => renderValue(row.lowerRange, styles),
      },
      {
        id: "upperRange",
        label: t("REGISTRATION.TABLE.UPPER_LIMIT"),
        align: "right",
        minWidth: 100,
        format: (row: RegistrationRow) => renderValue(row.upperRange, styles),
      },
      {
        id: "axleLoad",
        label: t("REGISTRATION.TABLE.FRONT_LOAD"),
        align: "right",
        minWidth: 100,
        format: (row: RegistrationRow) => renderValue(row.axleLoad, styles),
      },
      {
        id: "backAxleDistance",
        label: t("REGISTRATION.TABLE.REAR_AXLES_MEASURE"),
        align: "right",
        minWidth: 120,
        format: (row: RegistrationRow) =>
          renderValue(row.backAxleDistance, styles),
      },
      {
        id: "remarks",
        label: t("REGISTRATION.TABLE.NOTES"),
        align: "right",
        minWidth: 80,
        format: (row: RegistrationRow) => renderValue(row.remarks, styles),
      },
      {
        id: "needEngPlan",
        label: t("REGISTRATION.TABLE.REQUIRES_PLAN"),
        align: "right",
        minWidth: 110,
        format: (row: RegistrationRow) =>
          renderValue(row.needEngPlan, styles),
      },
      {
        id: "structureType",
        label: t("REGISTRATION.TABLE.DH_TYPE"),
        align: "right",
        minWidth: 88,
        format: (row: RegistrationRow) =>
          renderValue(row.structureType, styles),
      },
      {
        id: "sideWallTypeID",
        label: t("REGISTRATION.TABLE.BODY_TYPE"),
        align: "right",
        minWidth: 96,
        format: (row: RegistrationRow) =>
          renderValue(row.sideWallTypeID, styles),
      },
    ],
    [styles, t]
  );

  return (
    <div className={styles.root} dir="rtl">
      <DataTable
        rows={rows}
        columns={columns}
        loading={loading && rows.length === 0}
        serialEnabled={false}
        noDataLabel={t("REGISTRATION.EMPTY")}
      />
    </div>
  );
};

export default RegistrationTable;
